/**
 * useWatchlist — manages the user's watchlist state.
 *
 * Exposes:
 *   - savedIds: Set<string> of school UUIDs currently in the watchlist.
 *   - loading: true while the initial watchlist fetch is in progress.
 *   - save(schoolId): optimistically adds a school to savedIds, then calls
 *     POST /api/v1/watchlist. Rolls back on failure.
 *   - remove(schoolId): optimistically removes a school from savedIds, then
 *     calls DELETE /api/v1/watchlist/:id. Rolls back on failure.
 *   - refresh(position?, targetYear?): re-fetches the watchlist (e.g. after
 *     position/year selector changes on WatchlistPage).
 *   - entries: full WatchlistEntry[] array — useful for WatchlistPage.
 *
 * This hook does not handle auth-prompt logic — callers that receive an error
 * with status 401 should show the AuthPromptSheet and retry after auth.
 *
 * Usage:
 *   const { savedIds, save, remove, loading, entries, refresh } = useWatchlist(isAuthenticated);
 */

import { useState, useEffect, useCallback } from 'react';
import {
  WatchlistEntry,
  WatchlistSaveOptions,
  addToWatchlist,
  removeFromWatchlist,
  getWatchlist,
} from '../api/watchlist';

export interface UseWatchlistResult {
  /** Set of school UUIDs the user has saved. */
  savedIds: Set<string>;
  /** True while the initial watchlist fetch is in progress. */
  loading: boolean;
  /** Full watchlist entry array (for WatchlistPage). */
  entries: WatchlistEntry[];
  /**
   * Save a school. Returns true on success.
   * Optionally captures search context (WI-44: sport, year, positions).
   * Throws WatchlistApiError (including 401 when unauthenticated).
   */
  save: (schoolId: string, options?: WatchlistSaveOptions) => Promise<void>;
  /**
   * Remove a school. Optimistic — rolls back on failure.
   * Throws WatchlistApiError on network failure.
   */
  remove: (schoolId: string) => Promise<void>;
  /**
   * Re-fetch the watchlist with optional position/year filter.
   * Used when position/year selectors change on WatchlistPage.
   */
  refresh: (position?: string, targetYear?: number) => Promise<void>;
}

export function useWatchlist(isAuthenticated: boolean): UseWatchlistResult {
  const [entries, setEntries] = useState<WatchlistEntry[]>([]);
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  // Fetch watchlist on mount and when auth state changes.
  const refresh = useCallback(
    async (position?: string, targetYear?: number) => {
      if (!isAuthenticated) {
        setEntries([]);
        setSavedIds(new Set());
        return;
      }
      setLoading(true);
      try {
        const data = await getWatchlist(position, targetYear);
        setEntries(data);
        setSavedIds(new Set(data.map((e) => e.school_id)));
      } catch {
        // Silently fail — the caller (SearchPage, etc.) shows results without
        // saved state rather than blocking the UI.
      } finally {
        setLoading(false);
      }
    },
    [isAuthenticated]
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  const save = useCallback(
    async (schoolId: string, options?: WatchlistSaveOptions) => {
      // Optimistic update.
      setSavedIds((prev) => { const next = new Set(prev); next.add(schoolId); return next; });
      try {
        await addToWatchlist(schoolId, options);
      } catch (err) {
        // Roll back on failure.
        setSavedIds((prev) => {
          const next = new Set(prev);
          next.delete(schoolId);
          return next;
        });
        throw err; // Re-throw so the caller can handle (e.g. show 401 prompt).
      }
    },
    []
  );

  const remove = useCallback(
    async (schoolId: string) => {
      // Optimistic update.
      setSavedIds((prev) => {
        const next = new Set(prev);
        next.delete(schoolId);
        return next;
      });
      setEntries((prev) => prev.filter((e) => e.school_id !== schoolId));
      try {
        await removeFromWatchlist(schoolId);
      } catch (err) {
        // Roll back on failure.
        // We can't easily restore the entry object here, so we re-fetch.
        refresh();
        throw err;
      }
    },
    [refresh]
  );

  return { savedIds, loading, entries, save, remove, refresh };
}
